import validator from 'validar-telefone'

import { ErrorType } from '@store/simulacaoSlice/type'

export { default as FontFamily } from './fontFamily'
export { default as Colors } from './colors'
export { default as Perguntas } from './perguntas'
export { default as redirects } from './redirects'
export { default as GoogleTags } from './GoogleTags'

export const isEmail = (email: string) =>
  new RegExp(
    /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/gi,
  ).test(email)

export const isTelefone = (telefone: string) => validator(telefone)
export const minimoSaldo = 100.0
// eslint-disable-next-line no-useless-escape
export const isNome = (nome: string) => new RegExp(/^[a-zÃ-ū]{2,}\ [a-zÃ-ū]{2,}/gi).test(nome)
export const minimoAnoDataNascimento = 1922

/**
 * Maximum input length to prevent ReDoS attacks
 * This limits the input size before applying regex patterns
 */
const MAX_INPUT_LENGTH_FOR_REGEX = 10000

/**
 * Removes control characters from a string (ASCII codes < 32, except \n and \t)
 * Uses character code checking instead of regex to avoid control characters in regex patterns
 * @param input - String that may contain control characters
 * @returns String without control characters
 */
const removeControlCharacters = (input: string): string => {
  let result = ''
  for (let i = 0; i < input.length; i++) {
    const charCode = input.charCodeAt(i)
    // Keep printable characters (>= 32), newline (10), and tab (9)
    // Remove other control characters (< 32, except 9 and 10) and DEL (127)
    if ((charCode >= 32 || charCode === 9 || charCode === 10) && charCode !== 127) {
      result += input[i]
    }
  }
  return result
}

/**
 * Removes all HTML tags from a string to prevent HTML injection
 * Protected against ReDoS by limiting input size before regex application
 * @param input - String that may contain HTML
 * @returns String without HTML tags
 */
export const sanitizeHtml = (input: string): string => {
  if (!input || typeof input !== 'string') return ''
  // Limit input size to prevent ReDoS attacks
  const limitedInput = input.length > MAX_INPUT_LENGTH_FOR_REGEX
    ? input.substring(0, MAX_INPUT_LENGTH_FOR_REGEX)
    : input
  // Use non-greedy quantifier with bounded repetition to prevent excessive backtracking
  return limitedInput.replace(/<[^>]{0,1000}>/g, '')
}

/**
 * Sanitizes string by removing HTML and control characters
 * Focuses on preventing HTML injection without being overly restrictive
 * Preserves valid spaces between words (single spaces)
 * @param input - String to be sanitized
 * @param maxLength - Maximum allowed length (default: 255)
 * @param trimSpaces - Whether to trim leading/trailing spaces (default: false, preserves spaces during typing)
 * @returns Sanitized string
 */
export const sanitizeString = (input: string, maxLength = 255, trimSpaces = false): string => {
  if (!input || typeof input !== 'string') return ''

  // Limit input size before regex to prevent ReDoS attacks
  const limitedInput = input.length > MAX_INPUT_LENGTH_FOR_REGEX
    ? input.substring(0, MAX_INPUT_LENGTH_FOR_REGEX)
    : input

  let sanitized = limitedInput
    // Remove HTML tags (main protection against HTML injection)
    // Use bounded repetition to prevent excessive backtracking (max 1000 chars between < and >)
    .replace(/<[^>]{0,1000}>/g, '')
    // Remove control characters using character code checking (avoids control chars in regex)
    // This is safer than using regex with control characters
    // Remove < and > characters that are dangerous for HTML injection
    .replace(/[<>]/g, '')
    // Normalize multiple spaces to single space (preserves valid spacing between words)
    // Safe from ReDoS: input already limited to MAX_INPUT_LENGTH_FOR_REGEX, and /\s+/g is efficient
    .replace(/\s+/g, ' ')

  // Remove control characters using function instead of regex
  sanitized = removeControlCharacters(sanitized)

  // Only trim if explicitly requested (not during typing)
  if (trimSpaces) {
    sanitized = sanitized.trim()
  }

  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength)
  }

  return sanitized
}

/**
 * Validates if name does not contain HTML or dangerous characters
 * Allows apostrophes and quotes that are valid in names
 * @param name - Name to be validated
 * @returns true if name is valid and safe
 */
export const isSafeName = (name: string): boolean => {
  if (!name || typeof name !== 'string') return false

  // Limit input size to prevent ReDoS attacks
  if (name.length > MAX_INPUT_LENGTH_FOR_REGEX) return false

  // Check if contains HTML tags (main risk)
  // Use bounded repetition to prevent excessive backtracking
  if (/<[^>]{0,1000}>/g.test(name)) return false

  // Check if contains < and > characters that are dangerous for HTML injection
  if (/[<>]/g.test(name)) return false

  // Validate basic name format (allows apostrophes and quotes)
  return isNome(name)
}

/**
 * Sanitizes email by removing HTML and dangerous characters
 * Focuses on preventing HTML injection while maintaining valid email format
 * @param email - Email to be sanitized
 * @returns Sanitized email
 */
export const sanitizeEmail = (email: string): string => {
  if (!email || typeof email !== 'string') return ''

  // Limit input size to prevent ReDoS attacks (RFC 5321 limit is 254, but we check before regex)
  const limitedEmail = email.length > MAX_INPUT_LENGTH_FOR_REGEX
    ? email.substring(0, MAX_INPUT_LENGTH_FOR_REGEX)
    : email

  return limitedEmail
    .replace(/<[^>]{0,1000}>/g, '') // Remove HTML tags (main protection) - bounded to prevent ReDoS
    .replace(/[<>]/g, '') // Remove < and > characters that are dangerous
    .trim()
    .substring(0, 254) // RFC 5321 limit
}

/**
 * Sanitizes UTM parameters from query string
 * @param utmParam - UTM parameter to be sanitized
 * @param maxLength - Maximum length (default: 100)
 * @returns Sanitized parameter
 */
export const sanitizeUtmParam = (utmParam: string, maxLength = 100): string => {
  if (!utmParam || typeof utmParam !== 'string') return ''

  return sanitizeString(utmParam, maxLength)
}

export const getCodeErro = (code: string | null | undefined): ErrorType => {
  switch (code) {
    case 'FORA_PERIODO_CONTRATACAO':
      return 'FORA_PERIODO_CONTRATACAO'
    case 'ERRO_MENOR_IDADE':
      return 'MENOR_IDADE'
    default:
      return 'ERRO'
  }
}
